import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useProducts, money } from "./useProducts";
import { PalestineCauseBanner } from "./PalestineCauseBanner";
import { PalestineCounter } from "./PalestineCounter";

const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.8, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] } }),
};

const DETAILS = [
  ["Chest", "FREE PALESTINE in crystal · Map in flag colours · Olive branch"],
  ["Details", "Keffiyeh crystal panels · Watermelon sleeves · Onyx heavyweight terry"],
];

export const PalestineSpotlight = () => {
  const products = useProducts();
  // Live prices come from the Free Palestine Set product (Admin → Products); fall back to any Palestine-edition set.
  const palestine = products.find((p) => p.slug === "free-palestine-set")
    || products.find((p) => p.category === "palestine" && p.options.length >= 3)
    || products.find((p) => p.slug === "palestine");
  const navigate = useNavigate();
  const openProduct = () => navigate(`/shop/palestine?open=${palestine.slug}`);

  if (!palestine) return null;
  const details = [
    ...DETAILS,
    ["Edition", palestine.stock ? `${palestine.stock} numbered sets · Never re-run` : "Limited edition · Never re-run"],
    ["Pre-order", palestine.options.map((o) => `${o.label.replace(/ only$/i, "")} ${money(o.price)}`).join(" · ")],
  ];
  return (
    <section data-testid="palestine-section" className="relative bg-[#0a0a0b] text-[#f5f4ee] py-24 sm:py-32 overflow-hidden">
      <span
        aria-hidden
        className="pointer-events-none select-none absolute -right-6 top-6 sm:top-10 font-display font-extrabold text-[34vw] sm:text-[22vw] leading-none text-[#f5f4ee]/[0.035]"
        dir="rtl"
      >
        فلسطين
      </span>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_20%_50%,rgba(255,42,133,0.12),transparent_70%)]" />

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8">
        <motion.div
          initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={fadeUp}
          className="mb-14 sm:mb-20"
        >
          <div className="relative aspect-[3/2] rounded-2xl sm:rounded-3xl overflow-hidden border border-[#f5f4ee]/10">
            <img
              data-testid="palestine-image"
              src="/palestine-trio-v2.jpg"
              alt="Deen District Palestine Edition — keffiyeh-pattern PALESTINE hoodie sets in black, white and pink on a city street at night"
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-4 left-4 sm:bottom-6 sm:left-6 flex items-center gap-3">
              <span className="font-mono-d text-[10px] uppercase tracking-[0.25em] text-white bg-[#ff2a85] px-3 py-1.5 rounded-full">Special Edition</span>
              <span data-testid="palestine-remaining" className="font-mono-d text-[10px] uppercase tracking-[0.2em] text-white bg-[#0a0a0b]/80 px-3 py-1.5 rounded-full">{palestine.remaining != null ? `${palestine.remaining} of ${palestine.stock} left` : "Limited edition"}</span>
            </div>
          </div>
        </motion.div>

        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="-mt-6 sm:-mt-10 mb-14 sm:mb-20 max-w-4xl space-y-4">
          <PalestineCauseBanner variant="dark" testId="palestine-cause-banner-spotlight" />
          <PalestineCounter variant="dark" testId="palestine-counter-spotlight" />
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-7">
          <motion.p custom={0} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
            className="font-mono-d text-[11px] uppercase tracking-[0.3em] text-[#ff2a85] mb-7 flex items-center gap-3">
            <span className="diamond" /> Palestine Special Edition
          </motion.p>
          <motion.h2 custom={1} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
            className="font-display font-extrabold uppercase leading-[0.95] text-4xl sm:text-5xl lg:text-6xl tracking-tight">
            Worn on the chest. <br />
            <span className="text-pink">Said out loud.</span>
          </motion.h2>
          <motion.p custom={2} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
            className="mt-7 font-serif-accent text-2xl sm:text-3xl text-[#f5f4ee]">
            Two words. Ten thousand crystals.
          </motion.p>
          <motion.div custom={3} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
            className="mt-8 space-y-5 max-w-xl text-[#f5f4ee]/70 text-base sm:text-lg leading-relaxed">
            <p>
              <span className="text-[#f5f4ee]">FREE PALESTINE</span> across the chest in
              hand-set crystal — not whispered, not hidden on a tag. Beneath it, the map in
              red, green, white and black, and an olive branch for the peace we keep praying for.
            </p>
            <p>
              Keffiyeh pattern runs the chest panel and both sleeves in crystal; watermelon
              slices line the arms. Onyx heavyweight terry, so every stone catches the light.
              We put it on the front on purpose. It's the first thing anyone sees.
            </p>
          </motion.div>

          <motion.dl custom={4} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
            className="mt-9 grid sm:grid-cols-2 gap-x-8 gap-y-4 max-w-xl">
            {details.map(([k, v]) => (
              <div key={k} className="border-t border-[#f5f4ee]/15 pt-3">
                <dt className="font-mono-d text-[10px] uppercase tracking-[0.25em] text-[#f5f4ee]/50">{k}</dt>
                <dd className="mt-1 text-sm text-[#f5f4ee]">{v}</dd>
              </div>
            ))}
          </motion.dl>
        </div>

        <motion.div custom={5} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
          className="lg:col-span-5 flex flex-col justify-end gap-6">
          <div className="border border-[#f5f4ee]/15 rounded-3xl p-7 sm:p-9 bg-[#f5f4ee]/[0.03] backdrop-blur-sm">
            <p className="font-mono-d text-[10px] uppercase tracking-[0.25em] text-[#f5f4ee]/50">Pre-order</p>
            <div className="mt-4 space-y-3">
              {palestine.options.map((o) => (
                <div key={o.id} className="flex items-center justify-between border-b border-[#f5f4ee]/10 pb-3 last:border-0">
                  <span className="font-display font-extrabold uppercase text-sm">{o.label}</span>
                  <span data-testid={`palestine-price-${o.id}`} className="font-mono-d text-sm text-[#ff2a85]">{money(o.price)}</span>
                </div>
              ))}
            </div>
            <button
              data-testid="palestine-preorder-button"
              onClick={openProduct}
              className="group mt-7 w-full px-8 py-4 rounded-full bg-[#ff2a85] text-white font-mono-d text-[11px] uppercase tracking-[0.2em] font-medium hover:bg-[#e60067] hover:shadow-[0_10px_40px_-8px_rgba(255,42,133,0.65)] transition-all duration-300 flex items-center justify-center gap-2"
            >
              Pre-Order the Palestine Edition
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>
        </motion.div>
        </div>
      </div>
    </section>
  );
};
